/**
 * Email Sync Status Component
 * 
 * Shows real-time sync progress, booking previews, and success celebration.
 * Provides enhanced UX for email synchronization.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Modal, Animated, Pressable, ScrollView, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { Portal } from '@gorhom/portal';
import type { ExtractedBooking } from '@/services/inboxSync';
import {
  saveSyncHistory,
  getSyncHistory,
  getSyncMetadata,
  type SyncHistoryEntry,
} from '@/services/emailSyncHistory';
import type { InboxProviderId } from '@/services/inboxSync';
import { formatDistanceToNow } from 'date-fns';
import { EmailSyncAnalytics } from './EmailSyncAnalytics';

type EmailSyncStatusProps = {
  visible: boolean;
  onClose: () => void;
  tripId: string;
  provider: InboxProviderId;
  syncSource?: 'trips' | 'plan' | 'wallet' | string;
  onSyncComplete?: (bookings: ExtractedBooking[]) => void;
  onSyncNow?: () => void; // Optional callback for manual sync trigger
  onSyncAnotherEmail?: () => void; // Optional callback to open provider picker / switch email
  /**
   * If true (default), automatically starts a fresh sync when the modal is opened
   * and whenever `provider` / `tripId` changes while visible.
   */
  autoStart?: boolean;
};

type SyncStage = 'connecting' | 'fetching' | 'processing' | 'complete' | 'error';
const SYNC_NOW_DEBOUNCE_MS = 1200;

export function EmailSyncStatus({
  visible,
  onClose,
  tripId,
  provider,
  syncSource,
  onSyncComplete,
  onSyncNow,
  onSyncAnotherEmail,
  autoStart = true,
}: EmailSyncStatusProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [stage, setStage] = useState<SyncStage>('connecting');
  const [progress, setProgress] = useState(0);
  const [bookings, setBookings] = useState<ExtractedBooking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryEntry[]>([]);
  const [syncMetadata, setSyncMetadata] = useState<{
    lastSyncAt: number | null;
    lastSyncSuccess: boolean;
    totalBookingsFound: number;
    syncCount: number;
  } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [manualSyncing, setManualSyncing] = useState(false);
  const [syncNowDebounced, setSyncNowDebounced] = useState(false);
  const syncSourceLabel = useMemo(() => {
    if (!syncSource) return null;
    if (syncSource === 'trips') return 'Started from Trips';
    if (syncSource === 'plan') return 'Started from Plan';
    if (syncSource === 'wallet') return 'Started from Wallet';
    return `Started from ${syncSource}`;
  }, [syncSource]);

  const progressAnim = useRef(new Animated.Value(0)).current;
  const bookingAnimations = useRef<Map<number, Animated.Value>>(new Map()).current;
  const prevVisibleRef = useRef<boolean>(visible);
  const lastAutoStartKeyRef = useRef<string | null>(null);
  const syncNowDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load sync history and metadata
  useEffect(() => {
    loadSyncHistory();
    loadSyncMetadata();
  }, [tripId, provider]);

  // Animate progress
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: progress,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [progress]);

  // Animate booking appearance
  useEffect(() => {
    bookings.forEach((_, index) => {
      if (!bookingAnimations.has(index)) {
        const animValue = new Animated.Value(0);
        bookingAnimations.set(index, animValue);
        
        Animated.timing(animValue, {
          toValue: 1,
          duration: 400,
          delay: index * 100,
          useNativeDriver: true,
        }).start();
      }
    });
  }, [bookings]);

  const loadSyncHistory = async () => {
    try {
      const history = await getSyncHistory(tripId, provider);
      setSyncHistory(history.slice(0, 10)); // Show last 10 entries
    } catch (error) {
      console.error('[EmailSyncStatus] Error loading sync history:', error);
    }
  };

  const loadSyncMetadata = async () => {
    try {
      const metadata = await getSyncMetadata(tripId, provider);
      setSyncMetadata(metadata);
    } catch (error) {
      console.error('[EmailSyncStatus] Error loading sync metadata:', error);
    }
  };

  const simulateProgress = async () => {
    // Stage 1: Connecting (0-20%)
    setStage('connecting');
    setProgress(10);
    await delay(500);
    setProgress(20);

    // Stage 2: Fetching emails (20-50%)
    setStage('fetching');
    await delay(800);
    setProgress(35);
    await delay(600);
    setProgress(50);

    // Stage 3: Processing (50-90%)
    setStage('processing');
    await delay(500);
    setProgress(60);
    await delay(400);
    setProgress(75);
    await delay(400);
    setProgress(90);
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const resetUiState = useCallback(() => {
    setStage('connecting');
    setProgress(0);
    setBookings([]);
    setError(null);
    setShowSuccess(false);
    setShowHistory(false);
    bookingAnimations.clear();
    setSyncStartTime(null);
    setMinimized(false);
    // Keep progressAnim in sync with progress state; it will animate from next updates.
    progressAnim.setValue(0);
  }, [bookingAnimations, progressAnim]);

  const startSync = useCallback(async () => {
    setStage('connecting');
    setProgress(0);
    setBookings([]);
    setError(null);
    setShowSuccess(false);
    setSyncStartTime(Date.now());

    try {
      console.log(`[EmailSyncStatus] Starting sync for trip ${tripId}, provider ${provider}`);
      
      // Simulate progress
      await simulateProgress();

      // Import the sync function dynamically to avoid circular dependencies
      const { syncInboxBookings } = await import('@/services/inboxSync');
      
      // Perform actual sync
      console.log(`[EmailSyncStatus] Calling syncInboxBookings...`);
      const result = await syncInboxBookings(tripId, provider);
      console.log(`[EmailSyncStatus] Sync result:`, result);

      const duration = syncStartTime ? Date.now() - syncStartTime : undefined;

      if (result.status === 'auth') {
        setError('Authentication required. Please connect your email account.');
        setStage('error');
        
        // Save to history
        await saveSyncHistory({
          id: `${tripId}-${provider}-${Date.now()}`,
          tripId,
          provider,
          timestamp: Date.now(),
          bookingsFound: 0,
          success: false,
          error: 'Authentication required',
          duration,
        });
        
        await loadSyncHistory();
        await loadSyncMetadata();
        return;
      }

      if (result.status === 'error') {
        const errorMessage = result.message || 'Sync failed';
        console.error(`[EmailSyncStatus] Sync failed for trip ${tripId}, provider ${provider}:`, errorMessage);
        console.error(`[EmailSyncStatus] Full error result:`, result);
        setError(errorMessage);
        setStage('error');
        
        // Save to history
        await saveSyncHistory({
          id: `${tripId}-${provider}-${Date.now()}`,
          tripId,
          provider,
          timestamp: Date.now(),
          bookingsFound: 0,
          success: false,
          error: errorMessage,
          duration,
        });
        
        await loadSyncHistory();
        await loadSyncMetadata();
        return;
      }

      // Success
      const foundBookings = result.data || [];
      
      // Animate bookings appearing
      for (let i = 0; i < foundBookings.length; i++) {
        await delay(200);
        setBookings(prev => [...prev, foundBookings[i]]);
        setProgress(90 + (i + 1) * (10 / Math.max(foundBookings.length, 1)));
      }

      setProgress(100);
      setStage('complete');
      
      // Show success screen
      setShowSuccess(true);

      // Save to history
      await saveSyncHistory({
        id: `${tripId}-${provider}-${Date.now()}`,
        tripId,
        provider,
        timestamp: Date.now(),
        bookingsFound: foundBookings.length,
        success: true,
        duration,
      });

      // Reload history and metadata
      await loadSyncHistory();
      await loadSyncMetadata();

      // Call completion callback
      if (onSyncComplete) {
        onSyncComplete(foundBookings);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      const errorStack = err instanceof Error ? err.stack : undefined;
      
      console.error(`[EmailSyncStatus] Exception during sync for trip ${tripId}, provider ${provider}:`, err);
      console.error(`[EmailSyncStatus] Error message:`, errorMessage);
      console.error(`[EmailSyncStatus] Error stack:`, errorStack);
      
      const duration = syncStartTime ? Date.now() - syncStartTime : undefined;
      setError(errorMessage);
      setStage('error');
      
      // Save error to history
      await saveSyncHistory({
        id: `${tripId}-${provider}-${Date.now()}`,
        tripId,
        provider,
        timestamp: Date.now(),
        bookingsFound: 0,
        success: false,
        error: errorMessage,
        duration,
      });
      
      await loadSyncHistory();
      await loadSyncMetadata();
    } finally {
      setSyncStartTime(null);
    }
  }, [bookingAnimations, onSyncComplete, provider, syncStartTime, tripId]);

  const handleManualSyncNow = useCallback(async () => {
    if (manualSyncing || syncNowDebounced) return;
    setManualSyncing(true);
    setSyncNowDebounced(true);
    if (syncNowDebounceTimerRef.current) {
      clearTimeout(syncNowDebounceTimerRef.current);
    }
    syncNowDebounceTimerRef.current = setTimeout(() => {
      setSyncNowDebounced(false);
      syncNowDebounceTimerRef.current = null;
    }, SYNC_NOW_DEBOUNCE_MS);

    try {
      await Promise.resolve(onSyncNow?.());
      await startSync();
    } finally {
      setManualSyncing(false);
    }
  }, [manualSyncing, onSyncNow, startSync, syncNowDebounced]);

  const handleTryAgain = useCallback(async () => {
    if (manualSyncing || syncNowDebounced) return;
    setManualSyncing(true);
    setSyncNowDebounced(true);
    if (syncNowDebounceTimerRef.current) {
      clearTimeout(syncNowDebounceTimerRef.current);
    }
    syncNowDebounceTimerRef.current = setTimeout(() => {
      setSyncNowDebounced(false);
      syncNowDebounceTimerRef.current = null;
    }, SYNC_NOW_DEBOUNCE_MS);

    try {
      await startSync();
    } finally {
      setManualSyncing(false);
    }
  }, [manualSyncing, startSync, syncNowDebounced]);

  // Auto-start sync when modal opens, and re-run when provider/tripId changes while visible.
  // We guard with a stable key so we don't re-trigger on parent re-renders.
  useEffect(() => {
    if (!visible) return;
    if (!autoStart) return;
    const key = `${tripId}:${provider}`;
    if (lastAutoStartKeyRef.current === key) return;
    lastAutoStartKeyRef.current = key;
    resetUiState();
    startSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, tripId, provider, autoStart, resetUiState, startSync]);

  // Reset when the modal transitions from open -> closed.
  useEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = visible;

    if (wasVisible && !visible) {
      lastAutoStartKeyRef.current = null;
      resetUiState();
    }
  }, [visible, resetUiState]);

  useEffect(() => {
    return () => {
      if (syncNowDebounceTimerRef.current) {
        clearTimeout(syncNowDebounceTimerRef.current);
        syncNowDebounceTimerRef.current = null;
      }
    };
  }, []);

  // NOTE: If minimized, do NOT force the modal open on completion.
  // We keep the user on their current tab; the pill updates to "complete/error" and can be tapped to view details.

  const getStageLabel = (): string => {
    switch (stage) {
      case 'connecting':
        return 'Connecting to your inbox...';
      case 'fetching':
        return 'Fetching emails from your inbox...';
      case 'processing':
        return 'Extracting bookings...';
      case 'complete':
        return 'Sync complete!';
      case 'error':
        return 'Sync failed';
      default:
        return 'Syncing...';
    }
  };

  const isFirstSync = useMemo(() => {
    if (!syncMetadata) return true;
    if (!syncMetadata.lastSyncAt) return true;
    return (syncMetadata.syncCount || 0) === 0;
  }, [syncMetadata]);

  const getStageIcon = (): keyof typeof Ionicons.glyphMap => {
    switch (stage) {
      case 'connecting':
        return 'link';
      case 'fetching':
        return 'mail';
      case 'processing':
        return 'search';
      case 'complete':
        return 'checkmark-circle';
      case 'error':
        return 'alert-circle';
      default:
        return 'sync';
    }
  };

  const getBookingTypeIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'flight':
        return 'airplane';
      case 'hotel':
        return 'bed';
      case 'car':
        return 'car';
      case 'restaurant':
        return 'restaurant';
      case 'activity':
        return 'ticket';
      default:
        return 'calendar';
    }
  };

  const formatDate = (date?: Date): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (showSuccess && stage === 'complete' && visible && !minimized) {
    return (
      <Modal visible={visible} transparent animationType="fade">
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.overlay,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Card
            variant="filled"
            style={{
              width: '90%',
              maxWidth: 400,
              padding: theme.spacing(3),
              alignItems: 'center',
            }}
          >
            <Animated.View
              style={{
                transform: [
                  {
                    scale: progressAnim.interpolate({
                      inputRange: [0, 100],
                      outputRange: [0.8, 1],
                    }),
                  },
                ],
              }}
            >
              <Ionicons
                name="checkmark-circle"
                size={80}
                color={theme.colors.success || theme.colors.primary}
              />
            </Animated.View>
            <SubHeading style={{ marginTop: theme.spacing(2), textAlign: 'center' }}>
              Successfully Synced!
            </SubHeading>
            <Body style={{ marginTop: theme.spacing(1), textAlign: 'center', color: theme.colors.textSecondary }}>
              Found {bookings.length} booking{bookings.length !== 1 ? 's' : ''} from your emails
            </Body>
            <View style={{ marginTop: theme.spacing(3), width: '100%', gap: theme.spacing(1) }}>
              {onSyncAnotherEmail && (
                <Button
                  label="Sync another email"
                  onPress={onSyncAnotherEmail}
                  variant="secondary"
                  icon={<Ionicons name="mail-outline" size={18} color={theme.colors.text} />}
                  style={{ width: '100%' }}
                />
              )}
              <Button
                label="Done"
                onPress={onClose}
                variant="primary"
                style={{ width: '100%' }}
              />
            </View>
          </Card>
        </View>
      </Modal>
    );
  }

  // Minimized view: keep sync running, but show a small floating pill that can be tapped to reopen.
  if (visible && minimized) {
    const pillLabel =
      stage === 'error'
        ? 'Sync failed — tap to view'
        : stage === 'complete'
          ? 'Sync complete — tap to view'
          : `Syncing ${provider.toUpperCase()}… ${Math.round(progress)}%`;

    return (
      <Portal hostName="floating-overlay">
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            left: theme.spacing(2),
            right: theme.spacing(2),
            bottom: (insets.bottom || 0) + 76,
            zIndex: 999,
            elevation: 20,
          }}
        >
          <Pressable
            onPress={() => setMinimized(false)}
            accessibilityRole="button"
            accessibilityLabel="Email sync in progress"
            accessibilityHint="Tap to reopen sync details"
            style={({ pressed }) => ({
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              borderRadius: theme.radii.lg,
              padding: theme.spacing(1.5),
              opacity: pressed ? 0.9 : 1,
              shadowColor: '#000',
              shadowOpacity: 0.18,
              shadowRadius: 14,
              shadowOffset: { width: 0, height: 10 },
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1, minWidth: 0 }}>
                <Ionicons
                  name={getStageIcon()}
                  size={20}
                  color={stage === 'error' ? theme.colors.error : theme.colors.primary}
                />
                <ThemedText variant="bodyStrong" style={{ flex: 1, minWidth: 0, fontSize: 14 }} numberOfLines={1}>
                  {pillLabel}
                </ThemedText>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="chevron-up" size={18} color={theme.colors.textSecondary} />
                <Pressable
                  onPress={() => {
                    setMinimized(false);
                    onClose();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss email sync status"
                  accessibilityHint="Closes the sync status window"
                  style={({ pressed }) => ({
                    padding: theme.spacing(0.5),
                    borderRadius: 999,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
                </Pressable>
              </View>
            </View>
            <View
              style={{
                marginTop: theme.spacing(1),
                height: 6,
                backgroundColor: theme.colors.borderMuted,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${Math.max(3, Math.min(100, Math.round(progress)))}%`,
                  backgroundColor: stage === 'error' ? theme.colors.error : theme.colors.primary,
                }}
              />
            </View>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), fontSize: 11 }}>
              {stage !== 'complete' && stage !== 'error'
                ? 'Fetching your inbox can take a little while • Tap for details • You can keep using the app'
                : 'Tap to view details • You can keep using the app'}
            </Caption>
          </Pressable>
        </View>
      </Portal>
    );
  }

  return (
    <Modal
      visible={visible && !minimized}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle="pageSheet"
    >
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
            flex: 1,
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), flex: 1 }}>
              <Ionicons
                name={getStageIcon()}
                size={24}
                color={stage === 'error' ? theme.colors.error : theme.colors.primary}
              />
              <View style={{ flexShrink: 1 }}>
                <SubHeading>{getStageLabel()}</SubHeading>
                {syncSourceLabel ? (
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                    {syncSourceLabel}
                  </Caption>
                ) : null}
                {(stage === 'fetching' || stage === 'processing') && (
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.75), lineHeight: 18 }}>
                    Fetching messages from your inbox can take a little while — especially with a busy mailbox.
                  </Caption>
                )}
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              {stage !== 'complete' && stage !== 'error' && (
                <Pressable
                  onPress={() => setMinimized(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Minimize email sync window"
                  accessibilityHint="Hides this window while sync continues"
                  style={{
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.background,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                  }}
                >
                  <Ionicons name="remove" size={20} color={theme.colors.textSecondary} />
                </Pressable>
              )}
              {(stage === 'complete' || stage === 'error') && onSyncNow && (
                <Pressable
                  onPress={handleManualSyncNow}
                  disabled={manualSyncing || syncNowDebounced}
                  style={{
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.primary + '20',
                    opacity: manualSyncing || syncNowDebounced ? 0.6 : 1,
                  }}
                >
                  {manualSyncing || syncNowDebounced ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <Ionicons name="refresh" size={20} color={theme.colors.primary} />
                  )}
                </Pressable>
              )}
              {stage === 'complete' || stage === 'error' ? (
                <Pressable onPress={onClose}>
                  <Ionicons name="close" size={24} color={theme.colors.text} />
                </Pressable>
              ) : null}
            </View>
          </View>

          {/* Sync Metadata Summary */}
          {syncMetadata && (stage === 'complete' || stage === 'error') && (
            <Card variant="outlined" style={{ margin: theme.spacing(2), marginBottom: 0 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Body variant="bodyStrong">Last Sync</Body>
                  {syncMetadata.lastSyncAt ? (
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                      {formatDistanceToNow(new Date(syncMetadata.lastSyncAt), { addSuffix: true })}
                    </Caption>
                  ) : (
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                      Never synced
                    </Caption>
                  )}
                </View>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <Body variant="bodyStrong">Total Bookings</Body>
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                    {syncMetadata.totalBookingsFound} found
                  </Caption>
                </View>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <Body variant="bodyStrong">Sync Count</Body>
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                    {syncMetadata.syncCount} time{syncMetadata.syncCount !== 1 ? 's' : ''}
                  </Caption>
                </View>
              </View>
              {onSyncNow && (
                <Button
                  label={manualSyncing || syncNowDebounced ? 'Syncing…' : 'Sync Now'}
                  onPress={handleManualSyncNow}
                  variant="secondary"
                  disabled={manualSyncing || syncNowDebounced}
                  style={{ marginTop: theme.spacing(1.5) }}
                  icon={
                    manualSyncing || syncNowDebounced ? (
                      <ActivityIndicator size="small" color={theme.colors.text} />
                    ) : (
                      <Ionicons name="sync-outline" size={18} color={theme.colors.text} />
                    )
                  }
                />
              )}
            </Card>
          )}

          {/* Progress Bar */}
          <View style={{ padding: theme.spacing(2) }}>
            {isFirstSync && stage !== 'complete' && stage !== 'error' && (
              <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1), textAlign: 'center' }}>
                The first time syncing might take a while.
              </Caption>
            )}
            <View
              style={{
                height: 8,
                backgroundColor: theme.colors.background,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Animated.View
                style={{
                  height: '100%',
                  width: progressAnim.interpolate({
                    inputRange: [0, 100],
                    outputRange: ['0%', '100%'],
                  }),
                  backgroundColor: stage === 'error' ? theme.colors.error : theme.colors.primary,
                }}
              />
            </View>
            <Caption style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
              {Math.round(progress)}%
            </Caption>
          </View>

          {/* Content */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: theme.spacing(2) }}
          >
            {error ? (
              <Card variant="outlined" style={{ padding: theme.spacing(2) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
                  <Body style={{ color: theme.colors.error, flex: 1 }}>{error}</Body>
                </View>
                <Button
                  label={manualSyncing || syncNowDebounced ? 'Syncing…' : 'Try Again'}
                  onPress={handleTryAgain}
                  variant="primary"
                  disabled={manualSyncing || syncNowDebounced}
                  style={{ marginTop: theme.spacing(2) }}
                  icon={
                    manualSyncing || syncNowDebounced ? (
                      <ActivityIndicator size="small" color={theme.colors.onPrimary} />
                    ) : (
                      <Ionicons name="refresh" size={18} color={theme.colors.onPrimary} />
                    )
                  }
                />
              </Card>
            ) : bookings.length > 0 ? (
              <>
                <Body style={{ marginBottom: theme.spacing(2), color: theme.colors.textSecondary }}>
                  Found {bookings.length} booking{bookings.length !== 1 ? 's' : ''}:
                </Body>
                {bookings.map((booking, index) => {
                  const animValue = bookingAnimations.get(index) || new Animated.Value(1);
                  
                  return (
                    <Animated.View
                      key={index}
                      style={{
                        opacity: animValue,
                        transform: [
                          {
                            translateY: animValue.interpolate({
                              inputRange: [0, 1],
                              outputRange: [20, 0],
                            }),
                          },
                        ],
                      }}
                    >
                      <Card
                        variant="outlined"
                        style={{
                          marginBottom: theme.spacing(1.5),
                          padding: theme.spacing(1.5),
                        }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1.5) }}>
                          <Ionicons
                            name={getBookingTypeIcon(booking.type)}
                            size={24}
                            color={theme.colors.primary}
                          />
                          <View style={{ flex: 1 }}>
                            <Body style={{ fontWeight: '600' }}>
                              {booking.provider} {booking.type}
                            </Body>
                            {booking.confirmationNumber && (
                              <Caption style={{ marginTop: 2 }}>
                                Confirmation: {booking.confirmationNumber}
                              </Caption>
                            )}
                            {(booking.departureDate || booking.checkInDate) && (
                              <Caption style={{ marginTop: 2 }}>
                                {formatDate(booking.departureDate || booking.checkInDate)}
                                {booking.arrivalDate && ` - ${formatDate(booking.arrivalDate)}`}
                                {booking.checkOutDate && ` - ${formatDate(booking.checkOutDate)}`}
                              </Caption>
                            )}
                            {booking.location && (
                              <Caption style={{ marginTop: 2 }}>📍 {booking.location}</Caption>
                            )}
                            {booking.amount && booking.currency && (
                              <Caption style={{ marginTop: 2 }}>
                                {booking.currency} {booking.amount.toFixed(2)}
                              </Caption>
                            )}
                          </View>
                          <Ionicons
                            name="checkmark-circle"
                            size={20}
                            color={theme.colors.success || theme.colors.primary}
                          />
                        </View>
                      </Card>
                    </Animated.View>
                  );
                })}
              </>
            ) : (
              <View style={{ alignItems: 'center', padding: theme.spacing(4) }}>
                <Body style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
                  {stage === 'connecting' && 'Establishing connection...'}
                  {stage === 'fetching' && 'Searching your inbox...'}
                  {stage === 'processing' && 'Analyzing emails...'}
                </Body>
                {(stage === 'fetching' || stage === 'processing') && (
                  <Caption
                    color={theme.colors.textSecondary}
                    style={{ marginTop: theme.spacing(1.5), textAlign: 'center', lineHeight: 18, paddingHorizontal: theme.spacing(2) }}
                  >
                    This can take a little while while messages are downloaded and scanned.
                  </Caption>
                )}
              </View>
            )}

            {/* Sync Analytics */}
            {stage === 'complete' && (
              <View style={{ marginTop: theme.spacing(3) }}>
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Analytics</SubHeading>
                <EmailSyncAnalytics tripId={tripId} provider={provider} />
              </View>
            )}

            {/* Sync History */}
            {syncHistory.length > 0 && (stage === 'complete' || stage === 'error') && (
              <View style={{ marginTop: theme.spacing(3) }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(1.5) }}>
                  <SubHeading>Sync History</SubHeading>
                  {syncHistory.length > 5 && (
                    <Pressable onPress={() => setShowHistory(!showHistory)}>
                      <Caption color={theme.colors.primary}>
                        {showHistory ? 'Show Less' : `Show All (${syncHistory.length})`}
                      </Caption>
                    </Pressable>
                  )}
                </View>
                {(showHistory ? syncHistory : syncHistory.slice(0, 5)).map((entry) => (
                  <Card
                    key={entry.id}
                    variant="outlined"
                    style={{
                      marginBottom: theme.spacing(1),
                      padding: theme.spacing(1.5),
                    }}
                  >
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Body style={{ fontSize: 13, fontWeight: '600' }}>
                            {new Date(entry.timestamp).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </Body>
                          <Ionicons
                            name={entry.success ? 'checkmark-circle' : 'close-circle'}
                            size={16}
                            color={entry.success ? (theme.colors.success || theme.colors.primary) : theme.colors.error}
                          />
                        </View>
                        <Caption style={{ marginTop: theme.spacing(0.5) }}>
                          {entry.bookingsFound} booking{entry.bookingsFound !== 1 ? 's' : ''} found
                          {entry.duration && ` • ${(entry.duration / 1000).toFixed(1)}s`}
                        </Caption>
                        {entry.error && (
                          <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                            {entry.error}
                          </Caption>
                        )}
                      </View>
                    </View>
                  </Card>
                ))}
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

